/**
 * Wording for typed-date corrections (Bill, 2026-08-03).
 *
 * Nothing the user types is ever changed silently: a clamped date is confirmed
 * before it is saved, and a range that cannot be honoured is refused with a
 * message that says why. Style follows the Gantt's existing limited-move confirm
 * (GanttChart.js), so the two read the same.
 *
 * One module so all four surfaces - the task modal on both client tabs, the admin
 * client view, and the Gantt's inline start cell - say the same thing.
 */

/** YYYY-MM-DD -> DD/MM/YYYY for display. */
function formatUK(ymd) {
    if (!ymd) return '';
    const [y, m, d] = String(ymd).split('-');
    return `${d}/${m}/${y}`;
}

const FIELD_LABEL = {
    start: 'start date',
    end: 'end date'
};

/**
 * Confirm one or more clamped dates. Returns true when the user accepts the
 * adjusted values, false when the caller must write nothing.
 * adjustments: [{ field, from, to, boundBy }] from clampTaskDateUpdates
 */
export function confirmClampAdjustments(adjustments) {
    if (!adjustments || adjustments.length === 0) return true;

    const lines = adjustments.map(a => {
        const label = FIELD_LABEL[a.field] || a.field;
        const by = a.boundBy ? `"${a.boundBy}"` : 'A linked task';
        return `${by} limits the ${label} to ${formatUK(a.to)} instead of ${formatUK(a.from)}.`;
    });

    const heading = adjustments.length === 1
        ? `This task's ${FIELD_LABEL[adjustments[0].field] || adjustments[0].field} is limited by a dependency.`
        : 'These dates are limited by dependencies.';

    return window.confirm(
        `${heading}\n\n` +
        `${lines.join('\n')}\n\n` +
        `Save the adjusted ${adjustments.length === 1 ? 'date' : 'dates'}?`
    );
}

/**
 * The typed range cannot fit between the tasks either side of it. There is no
 * legal outcome to offer, so this states the problem and nothing is saved.
 */
export function alertImpossibleRange({ typedDays, availableDays, boundBy }) {
    const by = boundBy ? `"${boundBy}"` : 'A linked task';
    const room = availableDays != null
        ? `Only ${availableDays} day${availableDays === 1 ? '' : 's'} are available between the tasks either side of it.`
        : 'There is not enough room between the tasks either side of it.';

    window.alert(
        `This task cannot be ${typedDays} day${typedDays === 1 ? '' : 's'} long.\n\n` +
        `${room}\n` +
        `${by} is the limiting task.\n\n` +
        `Nothing has been saved. Shorten the task, or change the dependency.`
    );
}

/**
 * The result would end before it starts (D9). Deliberately distinct from
 * alertImpossibleRange - this is an invalid range, not a gap problem, and must
 * never be reported as one.
 *
 * Two cases. Typed directly, it is an ordinary typo. Produced by a clamp, the
 * user typed something reasonable and a dependency pushed it past the task's
 * other date - naming only the clamped value would baffle them, so all three
 * dates appear.
 */
export function alertEndBeforeStart({ start, end, typed, clampedBy }) {
    if (typed) {
        const by = clampedBy ? `"${clampedBy}"` : 'A linked task';
        const movedStart = typed !== start;
        window.alert(
            `You entered ${formatUK(typed)} as the ${movedStart ? 'start' : 'end'} date.\n\n` +
            `${by} ${movedStart ? 'must finish first, which moves it to' : 'must start after this one, which moves it to'} ` +
            `${formatUK(movedStart ? start : end)} - ${movedStart ? 'after' : 'before'} this task's ` +
            `${movedStart ? 'end' : 'start'} date of ${formatUK(movedStart ? end : start)}.\n\n` +
            `Nothing has been saved. Adjust the other date first, or change the dependency.`
        );
        return;
    }

    window.alert(
        `The end date is before the start date.\n\n` +
        `Start: ${formatUK(start)}\n` +
        `End: ${formatUK(end)}\n\n` +
        `Nothing has been saved.`
    );
}
